import { Carousel, Lightbox, MediaContainer } from '@design-system';
import React, { useMemo } from 'react';

import { ImageSizes } from '../../../helpers/images';
import {
  TrackCarouselSlide,
  TrackMultiMediaClick,
} from '../../../utils/analytics';
import { storyblokMediaItemToLightboxEntry } from '../../../utils/lightbox';
import { renderStoryblokRichText } from '../../../utils/renderStoryblokRichText';
import { Image } from '../../Image';

type Props = {
  config: MultiMediaCarousel;
  perPage?: number;
  carouselArrowPosition?: 'top' | 'middle';
  imageSizes?: ImageSizes;
  showCaption?: boolean;
  analytics?: Analytics;
  eagerLoading?: boolean;
  headline?: string | null;
};

const MultiMediaCarousel: React.FC<Props> = ({
  analytics,
  carouselArrowPosition,
  config,
  eagerLoading,
  headline,
  imageSizes,
  perPage = 4,
  showCaption = true,
}) => {
  const lightboxItems = useMemo(
    () => config.items.map(storyblokMediaItemToLightboxEntry),
    [config.items],
  );

  return (
    <Lightbox items={lightboxItems}>
      {(lightbox) => (
        <Carousel
          arrowPosition={carouselArrowPosition}
          headline={headline}
          items={config.items.map((mediaItem, index) => (
            <div className="group relative overflow-hidden">
              {mediaItem.component === 'image' && (
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    lightbox.openOnSlide(index);
                    analytics &&
                      TrackMultiMediaClick({
                        page: analytics.page,
                        pageTitle: analytics.pageTitle,
                        context: analytics.context,
                        type: 'image',
                        title: mediaItem.title
                          ? mediaItem.title
                          : mediaItem.image.title,
                        position: index,
                      });
                  }}
                >
                  <MediaContainer
                    mediaComponent={
                      <Image
                        alt={mediaItem.image.alt}
                        eagerLoading={eagerLoading && index === 0}
                        focus={mediaItem.image.focus}
                        hoverEffect
                        shape="rounded"
                        size={imageSizes}
                        src={mediaItem.image.filename}
                      />
                    }
                    type="Image"
                  />
                  {showCaption && mediaItem.caption && (
                    <div className="mt-3 block text-neutral-4">
                      {renderStoryblokRichText(mediaItem.caption)}
                    </div>
                  )}
                </div>
              )}

              {mediaItem.component === 'Video' && (
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    lightbox.openOnSlide(index);
                    analytics &&
                      TrackMultiMediaClick({
                        page: analytics.page,
                        context: analytics.context,
                        type: 'video',
                        durationInSeconds:
                          mediaItem?.vimeo?.vimeo_oembed?.response.duration,
                        title: mediaItem.title
                          ? mediaItem.title
                          : mediaItem?.vimeo?.vimeo_oembed?.response.title,
                        position: index,
                      });
                  }}
                >
                  <MediaContainer
                    duration={mediaItem.vimeo.vimeo_oembed.response.duration}
                    mediaComponent={
                      <Image
                        alt={mediaItem.coverImage?.alt}
                        eagerLoading={eagerLoading}
                        focus={mediaItem.coverImage.focus}
                        hoverEffect
                        shape="rounded"
                        size={imageSizes}
                        src={mediaItem.coverImage?.filename}
                      />
                    }
                    title={mediaItem.title}
                    type="Video"
                  />
                </div>
              )}
            </div>
          ))}
          onSlide={(index: number) => {
            analytics &&
              TrackCarouselSlide({
                page: analytics.page,
                pageTitle: analytics.pageTitle,
                context: analytics.context,
                title: analytics.context,
                position: index,
                carouselLength: config.items.length,
              });
          }}
          perPage={perPage}
        />
      )}
    </Lightbox>
  );
};

export default MultiMediaCarousel;
